/**
 * FormSection.tsx
 *
 * Component for rendering collapsible sections in forms.
 * Supports:
 * - Collapsible/expandable sections
 * - Visual validation error indicators for required fields
 * - Required field validation status
 */

import React, { useState } from 'react';
import { Section } from '../../types/types';
import { ChevronDown, ChevronRight, AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';

interface FormSectionProps {
  section: Section;
  children: React.ReactNode;
  className?: string;

  // Collapse/expand functionality
  collapsed?: boolean;
  onCollapseChange?: (sectionId: string, collapsed: boolean) => void;

  // Validation error display
  hasValidationErrors?: boolean;
  invalidFieldCount?: number;
}

export const FormSection: React.FC<FormSectionProps> = ({
  section,
  children,
  className = '',
  collapsed,
  onCollapseChange,
  hasValidationErrors = false,
  invalidFieldCount = 0,
}) => {
  // Determine if section is collapsible
  const isCollapsible = section.collapsible ?? false;

  // Use controlled state if onCollapseChange provided, otherwise use local state
  const [localCollapsed, setLocalCollapsed] = useState(collapsed ?? section.collapsed ?? false);
  const effectiveCollapsed = collapsed !== undefined ? collapsed : localCollapsed;

  const handleToggleCollapse = () => {
    const newState = !effectiveCollapsed;

    if (collapsed === undefined) {
      setLocalCollapsed(newState);
    }

    onCollapseChange?.(section.id, newState);
  };

  return (
    <div
      className={cn('form-section py-6 my-6 px-4', className)}
      data-testid={`section-${section.id}`}
    >
      {/* Section Header */}
      <div className="mb-6">
        <div className="flex items-start gap-3">
          {/* Collapse/Expand Button - Only show if collapsible */}
          {isCollapsible && (
            <button
              onClick={handleToggleCollapse}
              className="flex-shrink-0 pt-1 text-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary rounded"
              aria-label={effectiveCollapsed ? "Expand section" : "Collapse section"}
              aria-expanded={!effectiveCollapsed}
            >
              {effectiveCollapsed ? (
                <ChevronRight className="h-5 w-5" />
              ) : (
                <ChevronDown className="h-5 w-5" />
              )}
            </button>
          )}

          {/* Title and Description Container */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-semibold text-foreground">
                {section.title}
              </h3>

              {/* Validation Error Badge */}
              {hasValidationErrors && (
                <div className="flex items-center gap-1 px-2 py-1 bg-red-50 border border-red-200 rounded-md">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                  <span className="text-xs font-medium text-red-600">
                    {invalidFieldCount} {invalidFieldCount === 1 ? 'field' : 'fields'} invalid
                  </span>
                </div>
              )}
            </div>

            {/* Section Description (if provided) */}
            {section.description && (
              <p className="text-sm text-muted-foreground mt-1">
                {section.description}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Section Content - Hidden when collapsed */}
      {!effectiveCollapsed && (
        <div
          className={cn(
            "space-y-4 border-t-2 border-b-2 pt-6 pb-6 -mx-4 px-4",
            hasValidationErrors
              ? "border-red-500 bg-red-50/30"
              : "border-border/50"
          )}
        >
          {children}
        </div>
      )}

      {/* Collapsed Indicator */}
      {effectiveCollapsed && (
        <div
          className={cn(
            "py-3 px-4 border-2 border-dashed rounded-md text-sm text-muted-foreground -mx-4",
            hasValidationErrors
              ? "border-red-500 bg-red-50/20"
              : "border-border/50"
          )}
        >
          <span className="italic">Section collapsed</span>
          {hasValidationErrors && (
            <span className="ml-2 text-red-600 font-medium">
              ({invalidFieldCount} {invalidFieldCount === 1 ? 'field needs' : 'fields need'} attention)
            </span>
          )}
        </div>
      )}
    </div>
  );
};
